import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@dental-staffing-platform/auth';
import { z } from 'zod';
import {
  createTaxDocumentInput,
  editTaxDocumentInput,
  taxDocumentActionInput,
  taxDocumentKind,
  taxRequestActionInput,
  type TaxDocument,
  type TaxRequestActionInput,
} from '@dental-staffing-platform/shared';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Pagination,
  Row,
  Select,
  Stack,
  Textarea,
} from '@dental-staffing-platform/ui';
import { taxAdminClient, taxPdfData } from '../lib/tax-client.js';
import { AdminApiError } from '../lib/admin-client.js';
import { useAsync } from '../lib/useAsync.js';

const retainedSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('create'), input: createTaxDocumentInput }),
  z.object({ kind: z.literal('edit'), id: z.string(), input: editTaxDocumentInput }),
  z.object({ kind: z.literal('action'), id: z.string(), input: taxDocumentActionInput }),
  z.object({ kind: z.literal('request'), id: z.string(), input: taxRequestActionInput }),
  z.object({
    kind: z.literal('upload'),
    id: z.string(),
    input: z.object({
      clientRequestId: z.string().uuid(),
      expectedRevision: z.number().int().positive(),
      sha: z.string().regex(/^[a-f0-9]{64}$/),
    }),
  }),
]);
type Pending = z.infer<typeof retainedSchema>;
function read(key: string) {
  try {
    const value = retainedSchema.safeParse(JSON.parse(sessionStorage.getItem(key) ?? 'null'));
    return value.success ? value.data : null;
  } catch {
    return null;
  }
}
const label = (value: string) => value.replaceAll('_', ' ');
export function TaxDocuments() {
  const { user } = useAuth();
  const key = `tax-admin:${user?.organizationId ?? ''}:${user?.id ?? ''}`;
  return <TaxAdminWorkspace key={key} storageKey={key} />;
}
function TaxAdminWorkspace({ storageKey }: { storageKey: string }) {
  const [params, setParams] = useSearchParams(),
    [tab, setTab] = useState<'documents' | 'requests'>(
      params.has('request') ? 'requests' : 'documents'
    );
  const [year, setYear] = useState(''),
    [search, setSearch] = useState(''),
    [status, setStatus] = useState(''),
    [page, setPage] = useState(1),
    [requestPage, setRequestPage] = useState(1),
    [historyPage, setHistoryPage] = useState(1);
  const [pending, setPending] = useState<Pending | null>(() => read(storageKey)),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [notice, setNotice] = useState(''),
    [discard, setDiscard] = useState(false),
    [retryFile, setRetryFile] = useState<File | null>(null);
  const [creating, setCreating] = useState(false),
    [replacement, setReplacement] = useState<TaxDocument | null>(null);
  const docs = useAsync(
    () =>
      taxAdminClient.documents({
        page,
        pageSize: 20,
        ...(year ? { year } : {}),
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
      }),
    [page, year, search, status]
  );
  const requests = useAsync(
    () =>
      taxAdminClient.requests({
        page: requestPage,
        pageSize: 20,
        ...(year ? { year } : {}),
        ...(search ? { search } : {}),
      }),
    [requestPage, year, search]
  );
  const selectedDoc = params.get('document'),
    selectedRequest = params.get('request');
  const detail = useAsync(
    () => (selectedDoc ? taxAdminClient.document(selectedDoc, historyPage) : Promise.resolve(null)),
    [selectedDoc, historyPage]
  );
  const request = useAsync(
    () =>
      selectedRequest
        ? taxAdminClient.request(selectedRequest, historyPage)
        : Promise.resolve(null),
    [selectedRequest, historyPage]
  );
  const store = (value: Pending | null) => {
    if (value) sessionStorage.setItem(storageKey, JSON.stringify(value));
    else sessionStorage.removeItem(storageKey);
    setPending(value);
  };
  const save = async (value: Pending, file?: File) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      let pdfBase64: string | undefined;
      if (value.kind === 'upload') {
        const selected = file ?? retryFile;
        if (!selected)
          throw new Error(
            'Reselect the same PDF to retry. Document bytes are never saved in browser storage.'
          );
        const data = await taxPdfData(selected);
        if (data.sha !== value.input.sha)
          throw new Error(
            'Choose the exact PDF from the retained upload. Its contents must match.'
          );
        pdfBase64 = data.pdfBase64;
        setRetryFile(selected);
      }
      store(value);
      const saved =
        value.kind === 'create'
          ? await taxAdminClient.create(value.input)
          : value.kind === 'edit'
            ? await taxAdminClient.edit(value.id, value.input)
            : value.kind === 'action'
              ? await taxAdminClient.action(value.id, value.input)
              : value.kind === 'request'
                ? await taxAdminClient.requestAction(value.id, value.input)
                : await taxAdminClient.upload(value.id, {
                    clientRequestId: value.input.clientRequestId,
                    expectedRevision: value.input.expectedRevision,
                    pdfBase64: pdfBase64 ?? '',
                  });
      store(null);
      setRetryFile(null);
      setCreating(false);
      setReplacement(null);
      setNotice('Tax record saved.');
      setHistoryPage(1);
      if (value.kind === 'request') {
        setTab('requests');
        setParams({ request: saved.id });
        requests.reload();
        request.reload();
      } else {
        setTab('documents');
        setParams({ document: saved.id });
        docs.reload();
        detail.reload();
      }
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'The tax record save could not be confirmed.'
      );
      if (cause instanceof AdminApiError && [400, 403, 404, 413, 422].includes(cause.status))
        store(null);
    } finally {
      setBusy(false);
    }
  };
  const download = async (id: string) => {
    setError('');
    try {
      await taxAdminClient.download(id);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The download failed.');
    }
  };
  const selectedDetail = detail.data?.document.id === selectedDoc ? detail.data : null;
  const selectedRequestDetail = request.data?.request.id === selectedRequest ? request.data : null;
  return (
    <Stack gap={24}>
      <PageHeader
        title="Tax documents"
        subtitle="Publish actual issuer-supplied PDFs and manage private document requests."
        actions={
          <Button
            disabled={busy || !!pending}
            onClick={() => {
              setCreating(true);
              setReplacement(null);
            }}
          >
            Create document draft
          </Button>
        }
      />
      <p>
        Files are private to the recipient and authorized organization operators. Do not create
        sample tax forms, enter tax IDs in metadata or messages, or treat publication as proof of
        government filing. PDF checks validate format, not issuer authenticity or tax accuracy.
      </p>
      {notice && <p role="status">{notice}</p>}
      {error && <p role="alert">{error}</p>}
      {pending && (
        <Card>
          <h2>Retained tax operation</h2>
          <p>
            The save may already have succeeded. Retry the same request, or check saved records
            before discarding it.
          </p>
          {pending.kind === 'upload' && (
            <Field label="Reselect retained PDF" htmlFor="tax-retry-pdf">
              <Input
                id="tax-retry-pdf"
                type="file"
                accept="application/pdf"
                onChange={(e) => setRetryFile(e.target.files?.[0] ?? null)}
              />
            </Field>
          )}
          <Stack gap={12}>
            <Button disabled={busy} onClick={() => void save(pending)}>
              Retry same tax operation
            </Button>
            <label>
              <input
                type="checkbox"
                checked={discard}
                onChange={(e) => setDiscard(e.target.checked)}
              />{' '}
              I checked saved records and understand the earlier save may have succeeded.
            </label>
            <Button
              variant="secondary"
              disabled={!discard || busy}
              onClick={() => {
                store(null);
                setDiscard(false);
                setRetryFile(null);
              }}
            >
              Discard retained operation
            </Button>
          </Stack>
        </Card>
      )}
      {creating && (
        <CreateDraft
          key={replacement?.id ?? 'new'}
          replacement={replacement}
          disabled={busy || !!pending}
          save={(input) => void save({ kind: 'create', input })}
          close={() => setCreating(false)}
        />
      )}
      <Row>
        <Button
          variant={tab === 'documents' ? 'primary' : 'secondary'}
          onClick={() => {
            setTab('documents');
            setPage(1);
          }}
        >
          Document register
        </Button>
        <Button
          variant={tab === 'requests' ? 'primary' : 'secondary'}
          onClick={() => setTab('requests')}
        >
          Document requests
        </Button>
      </Row>
      <Row style={{ flexWrap: 'wrap' }}>
        <Field label="Tax year filter" htmlFor="admin-tax-year">
          <Input
            id="admin-tax-year"
            type="number"
            min={2000}
            max={2100}
            placeholder="All years"
            value={year}
            onChange={(e) => {
              setYear(e.target.value);
              setPage(1);
              setRequestPage(1);
            }}
          />
        </Field>
        <Field label="Search tax records" htmlFor="admin-tax-search">
          <Input
            id="admin-tax-search"
            type="search"
            maxLength={100}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
              setRequestPage(1);
            }}
          />
        </Field>
        {tab === 'documents' && (
          <Field label="Document status" htmlFor="admin-tax-status">
            <Select
              id="admin-tax-status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All statuses</option>
              {['draft', 'published', 'withdrawn', 'superseded'].map((s) => (
                <option key={s} value={s}>
                  {label(s)}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Button
          variant="secondary"
          onClick={() => {
            docs.reload();
            requests.reload();
            detail.reload();
            request.reload();
          }}
        >
          Refresh tax records
        </Button>
      </Row>
      {tab === 'documents' ? (
        <>
          {docs.loading ? (
            <p role="status">Loading tax documents...</p>
          ) : docs.error ? (
            <Card>
              <p role="alert">{docs.error}</p>
              <Button onClick={docs.reload}>Retry documents</Button>
            </Card>
          ) : docs.data?.items.length ? (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,340px),1fr))',
                  gap: 16,
                }}
              >
                {docs.data.items.map((d) => (
                  <Card key={d.id}>
                    <h2>{d.title}</h2>
                    <Badge>{label(d.status)}</Badge>
                    <p>
                      {d.professionalName} · {d.year} · {label(d.kind).toUpperCase()}
                    </p>
                    <p>
                      {d.issuer} · {d.hasFile ? 'Private PDF attached' : 'PDF not attached'}
                    </p>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setParams({ document: d.id });
                        setHistoryPage(1);
                      }}
                    >
                      Manage tax document
                    </Button>
                  </Card>
                ))}
              </div>
              <Pagination {...docs.data} itemLabel="tax documents" onPageChange={setPage} />
            </>
          ) : (
            <EmptyState
              title="No tax documents"
              description="Create a draft for the correct professional and upload an actual issuer PDF before publishing."
            />
          )}
          {selectedDoc &&
            (detail.loading ? (
              <p>Loading document...</p>
            ) : detail.error ? (
              <Card>
                <p role="alert">{detail.error}</p>
                <Button onClick={detail.reload}>Retry document</Button>
              </Card>
            ) : (
              selectedDetail && (
                <Card>
                  <Stack gap={16}>
                    <h2>Manage {selectedDetail.document.title}</h2>
                    <p>
                      {selectedDetail.document.professionalName} · {selectedDetail.document.year} ·{' '}
                      {label(selectedDetail.document.status)} · revision{' '}
                      {selectedDetail.document.revision}
                    </p>
                    {selectedDetail.document.hasFile && (
                      <Button
                        variant="secondary"
                        onClick={() => void download(selectedDetail.document.id)}
                      >
                        Download private PDF
                      </Button>
                    )}
                    {selectedDetail.document.status === 'draft' && (
                      <DraftEditor
                        key={`${selectedDetail.document.id}:${selectedDetail.document.revision}`}
                        document={selectedDetail.document}
                        disabled={busy || !!pending}
                        edit={(input) =>
                          void save({ kind: 'edit', id: selectedDetail.document.id, input })
                        }
                        upload={async (file) => {
                          try {
                            const data = await taxPdfData(file);
                            await save(
                              {
                                kind: 'upload',
                                id: selectedDetail.document.id,
                                input: {
                                  clientRequestId: crypto.randomUUID(),
                                  expectedRevision: selectedDetail.document.revision,
                                  sha: data.sha,
                                },
                              },
                              file
                            );
                          } catch (cause) {
                            setError(cause instanceof Error ? cause.message : 'The upload failed.');
                          }
                        }}
                      />
                    )}
                    {['draft', 'published'].includes(selectedDetail.document.status) && (
                      <DocumentAction
                        key={`${selectedDetail.document.id}:action:${selectedDetail.document.revision}`}
                        document={selectedDetail.document}
                        disabled={busy || !!pending}
                        save={(input) =>
                          void save({ kind: 'action', id: selectedDetail.document.id, input })
                        }
                      />
                    )}{' '}
                    {selectedDetail.document.status === 'published' && (
                      <Button
                        variant="secondary"
                        disabled={busy || !!pending}
                        onClick={() => {
                          setReplacement(selectedDetail.document);
                          setCreating(true);
                        }}
                      >
                        Create replacement draft
                      </Button>
                    )}
                    <h3>Retained document history</h3>
                    {selectedDetail.history.items.map((e) => (
                      <article key={e.id}>
                        <strong>{label(e.kind)}</strong>
                        <p>
                          {e.actorName} · {new Date(e.recordedAt).toLocaleString()}
                        </p>
                        <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{e.body}</p>
                      </article>
                    ))}
                    <Pagination
                      {...selectedDetail.history}
                      itemLabel="document updates"
                      onPageChange={setHistoryPage}
                    />
                    <Button variant="secondary" onClick={() => setParams({})}>
                      Close document details
                    </Button>
                  </Stack>
                </Card>
              )
            ))}
        </>
      ) : (
        <>
          {requests.loading ? (
            <p role="status">Loading tax requests...</p>
          ) : requests.error ? (
            <Card>
              <p role="alert">{requests.error}</p>
              <Button onClick={requests.reload}>Retry requests</Button>
            </Card>
          ) : requests.data?.items.length ? (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,340px),1fr))',
                  gap: 16,
                }}
              >
                {requests.data.items.map((r) => (
                  <Card key={r.id}>
                    <h2>{r.subject}</h2>
                    <p>
                      {r.professionalName} · {r.year} · {label(r.status)}
                    </p>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setParams({ request: r.id });
                        setHistoryPage(1);
                      }}
                    >
                      Review tax request
                    </Button>
                  </Card>
                ))}
              </div>
              <Pagination
                {...requests.data}
                itemLabel="tax requests"
                onPageChange={setRequestPage}
              />
            </>
          ) : (
            <EmptyState
              title="No document requests"
              description="Professional requests for missing or corrected tax documents appear here."
            />
          )}
          {selectedRequest &&
            (request.loading ? (
              <p>Loading tax request...</p>
            ) : request.error ? (
              <Card>
                <p role="alert">{request.error}</p>
                <Button onClick={request.reload}>Retry request</Button>
              </Card>
            ) : (
              selectedRequestDetail && (
                <Card>
                  <Stack gap={16}>
                    <h2>{selectedRequestDetail.request.subject}</h2>
                    <p>
                      {selectedRequestDetail.request.professionalName} ·{' '}
                      {selectedRequestDetail.request.year} ·{' '}
                      {label(selectedRequestDetail.request.status)}
                    </p>
                    {selectedRequestDetail.request.documentId && (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setParams({ document: selectedRequestDetail.request.documentId ?? '' });
                          setTab('documents');
                          setHistoryPage(1);
                        }}
                      >
                        Open linked tax document
                      </Button>
                    )}
                    {selectedRequestDetail.history.items.map((e) => (
                      <article key={e.id}>
                        <strong>{label(e.kind)}</strong>
                        <p>
                          {e.actorName} · {new Date(e.recordedAt).toLocaleString()}
                        </p>
                        <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{e.body}</p>
                      </article>
                    ))}
                    <Pagination
                      {...selectedRequestDetail.history}
                      itemLabel="request updates"
                      onPageChange={setHistoryPage}
                    />
                    <RequestReply
                      key={`${selectedRequestDetail.request.id}:${selectedRequestDetail.request.revision}`}
                      actions={selectedRequestDetail.request.availableActions}
                      disabled={busy || !!pending}
                      save={(action, body) =>
                        void save({
                          kind: 'request',
                          id: selectedRequestDetail.request.id,
                          input: {
                            clientRequestId: crypto.randomUUID(),
                            expectedRevision: selectedRequestDetail.request.revision,
                            action,
                            body,
                          },
                        })
                      }
                    />
                    <Button variant="secondary" onClick={() => setParams({})}>
                      Close request details
                    </Button>
                  </Stack>
                </Card>
              )
            ))}
        </>
      )}
    </Stack>
  );
}
function CreateDraft({
  replacement,
  disabled,
  save,
  close,
}: {
  replacement: TaxDocument | null;
  disabled: boolean;
  save: (input: z.infer<typeof createTaxDocumentInput>) => void;
  close: () => void;
}) {
  const [search, setSearch] = useState(''),
    [page, setPage] = useState(1),
    [professionalId, setProfessionalId] = useState(replacement?.professionalId ?? '');
  const [year, setYear] = useState(String(replacement?.year ?? new Date().getFullYear() - 1)),
    [kind, setKind] = useState<TaxDocument['kind']>(replacement?.kind ?? '1099_nec');
  const [title, setTitle] = useState(replacement ? `Corrected ${replacement.title}` : ''),
    [issuer, setIssuer] = useState(replacement?.issuer ?? ''),
    [error, setError] = useState('');
  const recipients = useAsync(
    () => taxAdminClient.recipients({ page, pageSize: 20, search }),
    [page, search]
  );
  return (
    <Card>
      <h2>{replacement ? 'Create replacement document' : 'Create document draft'}</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const parsed = createTaxDocumentInput.safeParse({
            clientRequestId: crypto.randomUUID(),
            professionalId,
            year: Number(year),
            kind,
            title,
            issuer,
            ...(replacement ? { supersedesId: replacement.id } : {}),
          });
          if (!parsed.success) {
            setError(parsed.error.issues[0]?.message ?? 'Check the draft.');
            return;
          }
          save(parsed.data);
        }}
      >
        <Stack gap={16}>
          {!replacement && (
            <>
              <Field label="Find tax recipient" htmlFor="tax-recipient-search">
                <Input
                  id="tax-recipient-search"
                  type="search"
                  disabled={disabled}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </Field>
              {recipients.loading ? (
                <p>Loading recipients...</p>
              ) : recipients.error ? (
                <>
                  <p role="alert">{recipients.error}</p>
                  <Button type="button" onClick={recipients.reload}>
                    Retry recipients
                  </Button>
                </>
              ) : (
                recipients.data && (
                  <>
                    <Field label="Tax document recipient" htmlFor="tax-recipient">
                      <Select
                        id="tax-recipient"
                        required
                        disabled={disabled}
                        value={professionalId}
                        onChange={(e) => setProfessionalId(e.target.value)}
                      >
                        <option value="">Choose a professional</option>
                        {recipients.data.items.map((p) => (
                          <option value={p.id} key={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Pagination
                      {...recipients.data}
                      itemLabel="recipients"
                      onPageChange={setPage}
                    />
                  </>
                )
              )}
            </>
          )}
          {replacement && (
            <p>
              Replacement for {replacement.professionalName}, {replacement.year}. The original stays
              available until this draft is published.
            </p>
          )}
          <Row style={{ flexWrap: 'wrap' }}>
            <Field label="Document tax year" htmlFor="tax-create-year">
              <Input
                id="tax-create-year"
                type="number"
                min={2000}
                max={2100}
                required
                disabled={disabled || !!replacement}
                value={year}
                onChange={(e) => setYear(e.target.value)}
              />
            </Field>
            <Field label="Tax document type" htmlFor="tax-create-kind">
              <Select
                id="tax-create-kind"
                disabled={disabled || !!replacement}
                value={kind}
                onChange={(e) => setKind(e.target.value as TaxDocument['kind'])}
              >
                {taxDocumentKind.options.map((k) => (
                  <option value={k} key={k}>
                    {label(k).toUpperCase()}
                  </option>
                ))}
              </Select>
            </Field>
          </Row>
          <Field label="Document title" htmlFor="tax-create-title">
            <Input
              id="tax-create-title"
              required
              minLength={3}
              maxLength={150}
              disabled={disabled}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>
          <Field label="Document issuer" htmlFor="tax-create-issuer">
            <Input
              id="tax-create-issuer"
              required
              minLength={2}
              maxLength={150}
              disabled={disabled}
              value={issuer}
              onChange={(e) => setIssuer(e.target.value)}
            />
          </Field>
          {error && <p role="alert">{error}</p>}
          <Row>
            <Button type="submit" disabled={disabled}>
              Save document draft
            </Button>
            <Button type="button" variant="secondary" onClick={close}>
              Close draft form
            </Button>
          </Row>
        </Stack>
      </form>
    </Card>
  );
}
function DraftEditor({
  document,
  disabled,
  edit,
  upload,
}: {
  document: TaxDocument;
  disabled: boolean;
  edit: (input: z.infer<typeof editTaxDocumentInput>) => void;
  upload: (file: File) => Promise<void>;
}) {
  const [title, setTitle] = useState(document.title),
    [issuer, setIssuer] = useState(document.issuer),
    [file, setFile] = useState<File | null>(null);
  return (
    <Stack gap={16}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          edit({
            clientRequestId: crypto.randomUUID(),
            expectedRevision: document.revision,
            title,
            issuer,
          });
        }}
      >
        <Stack gap={12}>
          <Field label="Edit document title" htmlFor="tax-edit-title">
            <Input
              id="tax-edit-title"
              required
              minLength={3}
              maxLength={150}
              disabled={disabled}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>
          <Field label="Edit document issuer" htmlFor="tax-edit-issuer">
            <Input
              id="tax-edit-issuer"
              required
              minLength={2}
              maxLength={150}
              disabled={disabled}
              value={issuer}
              onChange={(e) => setIssuer(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={disabled}>
            Save draft details
          </Button>
        </Stack>
      </form>
      {!document.hasFile && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (file) void upload(file);
          }}
        >
          <Stack gap={12}>
            <Field label="Issuer PDF" htmlFor="tax-upload">
              <Input
                id="tax-upload"
                type="file"
                accept="application/pdf"
                required
                disabled={disabled}
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </Field>
            <p>
              PDF only, up to 3 MB. The uploaded file is retained. Use a replacement draft to supply
              a different file.
            </p>
            <Button type="submit" disabled={disabled || !file}>
              Upload private PDF
            </Button>
          </Stack>
        </form>
      )}
    </Stack>
  );
}
function DocumentAction({
  document,
  disabled,
  save,
}: {
  document: TaxDocument;
  disabled: boolean;
  save: (input: z.infer<typeof taxDocumentActionInput>) => void;
}) {
  const [reason, setReason] = useState(''),
    [confirmed, setConfirmed] = useState(false),
    [action, setAction] = useState<'publish' | 'withdraw'>(
      document.status === 'draft' ? 'publish' : 'withdraw'
    );
  const publish = action === 'publish';
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save({
          clientRequestId: crypto.randomUUID(),
          expectedRevision: document.revision,
          action: publish ? 'publish' : 'withdraw',
          reason,
          issuerDocumentConfirmed: confirmed,
        });
      }}
    >
      <Stack gap={12}>
        {document.status === 'draft' && (
          <Field label="Draft action" htmlFor="tax-draft-action">
            <Select
              id="tax-draft-action"
              value={action}
              disabled={disabled}
              onChange={(e) => setAction(e.target.value as 'publish' | 'withdraw')}
            >
              <option value="publish">Publish document</option>
              <option value="withdraw">Withdraw unused draft</option>
            </Select>
          </Field>
        )}
        <Field
          label={publish ? 'Publication note' : 'Withdrawal reason'}
          htmlFor="tax-action-reason"
        >
          <Textarea
            id="tax-action-reason"
            required
            minLength={3}
            maxLength={2000}
            disabled={disabled}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
        {publish && (
          <label>
            <input
              type="checkbox"
              checked={confirmed}
              disabled={disabled}
              onChange={(e) => setConfirmed(e.target.checked)}
            />{' '}
            I confirmed this is the actual issuer-supplied document for this professional and tax
            year.
          </label>
        )}
        <Button type="submit" disabled={disabled || (publish && (!document.hasFile || !confirmed))}>
          {publish ? 'Publish tax document' : 'Withdraw tax document'}
        </Button>
      </Stack>
    </form>
  );
}
function RequestReply({
  actions,
  disabled,
  save,
}: {
  actions: TaxRequestActionInput['action'][];
  disabled: boolean;
  save: (action: TaxRequestActionInput['action'], body: string) => void;
}) {
  const [action, setAction] = useState<TaxRequestActionInput['action']>(actions[0] ?? 'comment'),
    [body, setBody] = useState('');
  if (!actions.length) return <p>This request is read-only.</p>;
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save(action, body);
      }}
    >
      <Stack gap={12}>
        <Field label="Tax request action" htmlFor="admin-tax-request-action">
          <Select
            id="admin-tax-request-action"
            disabled={disabled}
            value={action}
            onChange={(e) => setAction(e.target.value as TaxRequestActionInput['action'])}
          >
            {actions.map((a) => (
              <option value={a} key={a}>
                {label(a)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Tax request reply" htmlFor="admin-tax-request-reply">
          <Textarea
            id="admin-tax-request-reply"
            required
            minLength={3}
            maxLength={2000}
            disabled={disabled}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </Field>
        <p>Replies are visible to the professional. Do not include tax IDs or bank information.</p>
        <Button type="submit" disabled={disabled}>
          Save request update
        </Button>
      </Stack>
    </form>
  );
}
